// Unpublished Work © 2025 Deere & Company.

import React, {useEffect} from 'react';
import {getOsName} from './helpers/get-os-name';
import {DataManagerPage} from './pages/DataManagerPage';
import {RedirectPage} from './pages/RedirectPage';
import {Route, Routes, Outlet} from 'react-router-dom';
import {SoftwareManagerPage} from './pages/SoftwareManagerPage';
import {PersistentHeader} from '@deere/persistent-header';
import {getLocationOrigin, getLocationPathname} from './helpers/location-helper';
import {RenderAppProps} from './types/session-type';

interface PersistentHeaderLayoutProps {
    sourceAppUri: string;
    contributionProductId: string;
    userId: string;
    url: string;
}

const PersistentHeaderLayout = ({sourceAppUri, contributionProductId, userId, url}: PersistentHeaderLayoutProps) => (
    <PersistentHeader
        contributionProductId={contributionProductId}
        userId={userId}
        sourceAppUri={sourceAppUri}
        url={url}
    >
        <Outlet />
    </PersistentHeader>
);

export const OpsCenterRouter = ({renderAppProps}: {renderAppProps: RenderAppProps}) => {
    const [operatingSystem, setOperatingSystem] = React.useState<string | undefined>();
    const sourceAppUri = `${getLocationOrigin()}${getLocationPathname()}`;

    const userId = renderAppProps.session.id;
    const {url, dataManagerContributionProductId, softwareManagerContributionProductId} =
        renderAppProps.session.persistentHeaderConfig;
    const routeToContributionProductId: Record<string, string> = {
        '/software-downloads/ads': dataManagerContributionProductId,
        '/software-downloads/software-manager': softwareManagerContributionProductId,
        '/software-downloads/i/software-manager': softwareManagerContributionProductId
    };
    const contributionProductId = routeToContributionProductId[getLocationPathname()] ?? '';

    useEffect(() => {
        setOperatingSystem(getOsName());
    }, []);

    return (
        <Routes>
            <Route element={<RedirectPage />} path={'/'} />
            <Route element={<RedirectPage />} path={'/login'} />
            <Route element={<RedirectPage />} path={'/licenseRedirect'} />
            <Route
                element={
                    <PersistentHeaderLayout
                        sourceAppUri={sourceAppUri}
                        contributionProductId={contributionProductId}
                        userId={userId}
                        url={url}
                    />
                }
            >
                <Route element={<DataManagerPage osName={operatingSystem} />} path={'/software-downloads/ads'} />
                <Route
                    element={<SoftwareManagerPage osName={operatingSystem} internal={true} />}
                    path={'/software-downloads/i/software-manager'}
                />
                <Route
                    element={<SoftwareManagerPage osName={operatingSystem} internal={false} />}
                    path={'/software-downloads/software-manager'}
                />
            </Route>
        </Routes>
    );
};
